import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [promo, setPromo] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/pricing").then((r) => {
      setTiers(r.data.tiers);
      setPromo(r.data.promo);
    });
  }, []);

  const isPro = user?.plan === "pro" || user?.plan === "lifetime";

  const startCheckout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen grain">
      <header className="border-b bg-card/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => nav("/dashboard")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back to calendar
          </button>
          {isPro && (
            <div className="flex items-center gap-2 text-sm bg-forest text-white px-3 py-1 rounded-full">
              <Crown className="w-3.5 h-3.5" strokeWidth={2} />
              {user.plan === "lifetime" ? "Lifetime member" : "Pro"}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          {promo && (
            <span
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-honey/20 text-honey border border-honey/40 px-3 py-1 rounded-full mb-4"
              data-testid="pricing-promo-badge"
            >
              <Sparkles className="w-3 h-3" strokeWidth={2} /> {promo.label} &middot; limited time
            </span>
          )}
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight">
            Speak <span className="text-terracotta">two</span>, four, or all <span className="text-honey">nine</span>.
          </h1>
          <p className="text-muted-foreground text-base mt-4 leading-relaxed">
            Free is generous — a fresh word every day in one language, seven days of history, and a five-day streak.
            Pro takes the guardrails off. {promo?.sub}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6" data-testid="pricing-tiers">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`tier-${t.id}`}
              className={`relative rounded-2xl border-2 p-8 bg-card ${
                t.highlighted ? "border-terracotta shadow-lg" : "border-border"
              }`}
            >
              {t.highlighted && (
                <div className="absolute -top-3 left-8 bg-terracotta text-white text-xs px-3 py-1 rounded-full uppercase tracking-widest font-medium">
                  Best value
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="font-display text-2xl">{t.name}</div>
                {t.savings_display && (
                  <span
                    className="text-[10px] uppercase tracking-widest bg-honey/20 text-honey border border-honey/40 px-2 py-0.5 rounded-full"
                    data-testid={`tier-savings-${t.id}`}
                  >
                    {t.savings_display}
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                {t.regular_price_display && (
                  <span className="text-2xl text-muted-foreground line-through" data-testid={`tier-regular-price-${t.id}`}>
                    {t.regular_price_display}
                  </span>
                )}
                <span className="font-display text-5xl tracking-tight text-terracotta" data-testid={`tier-price-${t.id}`}>
                  {t.price_display}
                </span>
                <span className="text-muted-foreground text-sm">{t.price_period}</span>
              </div>
              <ul className="mt-6 space-y-3">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-terracotta shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startCheckout(t.lookup_key)}
                disabled={loadingKey !== null || isPro}
                data-testid={`checkout-button-${t.id}`}
                className={`w-full rounded-full h-11 mt-8 ${
                  t.highlighted
                    ? "bg-terracotta hover:bg-terracotta/90 text-white"
                    : "bg-secondary hover:bg-secondary/90 text-white"
                }`}
              >
                {isPro ? "You're already Pro" : loadingKey === t.lookup_key ? "Redirecting…" : t.cta}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-xs text-muted-foreground max-w-lg mx-auto flex items-center justify-center gap-2">
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
          Test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.
        </div>
      </main>
    </div>
  );
}
